import React, { useMemo } from 'react';
import Resources from 'util/resource/Resources';
import SectionHeadingLarge from 'ui/elements/SectionHeading/SectionHeadingLarge';
import { useCompanies } from 'apis/CompanyAPI/companies/useCompanies';
import { useCommunities } from 'apis/CompanyAPI/communities/useCommunities';
import { addDays, startOfDay } from 'date-fns';
import { useUserFeedSWR, UserFeedAudience } from 'pages/Dashboard/useUserFeedSWR';
import PulseItems from './PulseItems';
import PopMenu from 'ui/modules/PopMenu';
import FilterIcon from 'ui/elements/icons/FilterIcon';
import ChevronDownIcon from 'ui/elements/icons/ChevronDownIcon';
import { Hidden } from '@mui/material';
import useSessionStorage from 'hooks/useSessionStorage';
import Button from 'ui/elements/buttons/Button';
import { Link } from 'react-router-dom';
import CalendarIcon from 'ui/elements/icons/CalendarIcon';
import IconAvatar from 'ui/elements/icons/IconAvatar';
import { CommunityListItem } from 'types/company/community';
import { PulseItemDTO } from 'apis/DashboardAPI';
import { CompanyOverviewSection, communityUrls } from 'urls';
import { quarterSpacing } from 'ui/theme/themeConstants';
import { bluePlanetTheme } from 'ui/theme';

export function getTitleAndHref(
  myCommunities: CommunityListItem[],
  item: PulseItemDTO,
): {
  title: React.ReactNode;
  href?: string;
  showCompanyIdInDialog?: { companyId: number; initialTab: CompanyOverviewSection };
  showEventIdInDialog?: UUID;
  logo: string | React.ReactNode;
} | null {
  switch (item.type) {
    case 'StartedFundingRound':
      return {
        title: `${item.company.name} started a new funding round`,
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'profile' },
        logo: item.company.logoURL,
      };
    case 'ClosedFundingRound':
      return {
        title: `${item.company.name} closed their funding round`,
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'profile' },
        logo: item.company.logoURL,
      };
    case 'UpdatedKpis':
      return {
        title: `${item.company.name} updated their metrics`,
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'kpis' },
        logo: item.company.logoURL,
      };
    case 'UpdatedProfile':
      return {
        title: `${item.company.name} updated their profile`,
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'profile' },
        logo: item.company.logoURL,
      };
    case 'UpdatedTeam':
      return {
        title: `${item.company.name} updated their team information`,
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'team' },
        logo: item.company.logoURL,
      };
    case 'UpdatedPitchDeck':
      return {
        title: `${item.company.name} uploaded a new pitch deck`,
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'profile' },
        logo: item.company.logoURL,
      };
    case 'PostedUpdate':
      const postedInCommunity = myCommunities
        .filter(c => c.roles.length > 0)
        .find(c => item.audienceCommunityIds.includes(c.id));

      return {
        title: postedInCommunity ? (
          <>
            {item.company.name} posted an update in{' '}
            <Link
              style={{ textDecoration: 'underline' }}
              to={communityUrls.overview(postedInCommunity.slug, 'updates')}
            >
              {postedInCommunity.details.name}
            </Link>
          </>
        ) : (
          `${item.company.name} posted an update`
        ),
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'updates' },
        logo: item.company.logoURL,
      };
    case 'JoinedCommunity':
      return {
        title: (
          <>
            {item.company.name} joined{' '}
            <Link style={{ textDecoration: 'underline' }} to={communityUrls.overview(item.communitySlug)}>
              {item.communityName}
            </Link>
          </>
        ),
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'profile' },
        logo: item.company.logoURL,
      };
    case 'CreatedEvent':
      return {
        title: `New event: ${item.title}`,
        showEventIdInDialog: item.eventId,
        logo: (
          <IconAvatar color="blue-light" size={{ width: '40px', height: '40px' }}>
            <CalendarIcon />
          </IconAvatar>
        ),
      };
  }
}

export const filter = ['All activity', 'Only posts', 'Only updates'] as const;
export type TFilter = (typeof filter)[number];
export const activityTypes = ['For you', 'From your network', 'From your collections'] as const;
export type ActivityType = (typeof activityTypes)[number];
export const DASHBOARD_PULSE_SELECTED_FEED_KEY = 'dashboard-pulse-selected-feed';

export default function Pulse({
  activityTypes,
  initialActivityType,
  newlyPostedUpdateId,
  enableScrollToUpdate,
}: {
  activityTypes: ActivityType[];
  initialActivityType?: ActivityType;
  newlyPostedUpdateId?: string;
  enableScrollToUpdate?: boolean;
}) {
  const [activeFilter, setActiveFilter] = React.useState<TFilter>(filter[0]);
  const companies = useCompanies();
  const communities = useCommunities();

  const startDate = useMemo(() => startOfDay(addDays(new Date(), -90)), []);

  const [selectedFeed, selectFeed] = useSessionStorage<ActivityType>(
    DASHBOARD_PULSE_SELECTED_FEED_KEY,
    initialActivityType || activityTypes[0],
  );
  const audience: UserFeedAudience | undefined =
    selectedFeed === 'For you'
      ? 'all'
      : selectedFeed === 'From your collections'
        ? 'shortlisted'
        : selectedFeed === 'From your network'
          ? 'following'
          : undefined;

  const { resource: feed } = useUserFeedSWR(startDate, { audience }, { limit: 50 });

  // Reusable button renderer for both selectors
  const renderFilterButton = (label: string, onClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void) => (
    <Button
      kind="tertiary"
      color="grey"
      onClick={onClick}
      size="small"
      style={{ border: `1px solid ${bluePlanetTheme.bluePlanetPalette.grey.medium}` }}
    >
      {label}
      <ChevronDownIcon className="u-quarter-spacing-left" fontSize="small" />
    </Button>
  );

  return (
    <>
      <Hidden smDown>
        <SectionHeadingLarge
          heading={<>{activityTypes.length > 1 ? 'Activity' : 'Activity ' + selectedFeed.toLocaleLowerCase()}</>}
        >
          <div className="u-flex u-flex-align-center" style={{ gap: quarterSpacing }}>
            {activityTypes.length > 1 && (
              <PopMenu
                items={activityTypes.map(f => {
                  return {
                    text: <>{f}</>,
                    onClick: () => selectFeed(f),
                    selected: f === selectedFeed,
                  };
                })}
                renderAnchor={onClick => renderFilterButton(selectedFeed, onClick)}
                icon={<FilterIcon />}
              />
            )}
            <PopMenu
              items={filter.map(f => {
                return {
                  text: <>{f}</>,
                  onClick: () => setActiveFilter(f),
                  selected: f === activeFilter,
                };
              })}
              renderAnchor={onClick => renderFilterButton(activeFilter, onClick)}
              icon={<FilterIcon />}
            />
          </div>
        </SectionHeadingLarge>
      </Hidden>

      <Hidden smUp>
        <div
          className="u-content-spacing-bottom u-half-spacing-x u-flex u-flex-end u-flex-align-center"
          style={{ gap: quarterSpacing }}
        >
          {activityTypes.length > 1 && (
            <PopMenu
              items={activityTypes.map(f => {
                return {
                  text: <>{f}</>,
                  onClick: () => selectFeed(f),
                  selected: f === selectedFeed,
                };
              })}
              renderAnchor={onClick => renderFilterButton(selectedFeed, onClick)}
              icon={<FilterIcon />}
            />
          )}
          <PopMenu
            items={filter.map(f => {
              return {
                text: <>{f}</>,
                onClick: () => setActiveFilter(f),
                selected: f === activeFilter,
              };
            })}
            renderAnchor={onClick => renderFilterButton(activeFilter, onClick)}
            icon={<FilterIcon />}
          />
        </div>
      </Hidden>
      <Resources resources={[companies.resource, communities.resource, feed]}>
        {([companies, communities, feed]) => (
          <PulseItems
            communities={communities.values}
            companies={companies.values}
            feed={feed.values}
            selectedFeed={selectedFeed}
            activeFilter={activeFilter}
            newlyPostedUpdateId={newlyPostedUpdateId}
            enableScrollToUpdate={enableScrollToUpdate}
          />
        )}
      </Resources>
    </>
  );
}
